import { type FC, type KeyboardEvent, type ReactNode, useCallback } from "react";

import { Alert, Button, Modal } from "@snapchat/snap-design-system";

import { useBlizzardEvents } from "../../../../analytics/hooks";
import { LensCreatorPayoutEligibility } from "../../../../gql-generated/types";
import { Flexbox } from "../../../../shared/components";
import { useToggleState } from "../../../../shared/hooks";
import { useDeleteLens } from "../../../hooks";

import { DeleteLensModal, LensDeleteHeader, LensDeleteText, deleteButtonStyle } from "./DeleteLensButton.styles";
import {
    LensCreatorPayoutAlertDescription,
    LensCreatorPayoutAlertTitle,
    ModalHeader,
    ModalText,
} from "./messages.generated";

interface DeleteLensButtonProps {
    lensId: string;
    lensCreatorPayoutEligibility: LensCreatorPayoutEligibility;
    isMeatballMenu?: boolean;
    children?: ReactNode;
    isDisabled?: boolean;
}

export const DeleteLensButton: FC<DeleteLensButtonProps> = ({
    lensId,
    lensCreatorPayoutEligibility,
    isMeatballMenu,
    children,
    isDisabled,
}) => {
    const [modalProps, showModal] = useToggleState();
    const { deleteLens, loading: deleteLensLoading } = useDeleteLens(lensId);
    const { sendBlizzardEvent } = useBlizzardEvents();

    const onClickDelete = useCallback(() => {
        if (isDisabled) {
            return;
        }

        deleteLens()
            .then(() => {
                sendBlizzardEvent({
                    event: "MYLENSES_MANAGEMENT_PAGE_ACTION",
                    pageType: isMeatballMenu ? "LENS_TABLE" : "LENS_DETAILS",
                    action: "DELETE_LENS",
                });
                modalProps.onCancel();
            })
            .catch(() => {
                // TODO - Error handling
            });
    }, [deleteLens, modalProps, sendBlizzardEvent, isMeatballMenu, isDisabled]);

    const onKeyPressModalTrigger = useCallback(
        ({ nativeEvent }: KeyboardEvent<HTMLDivElement>) => {
            if (nativeEvent.key !== "Enter" || isDisabled) {
                return;
            }
            showModal();
        },
        [showModal, isDisabled],
    );

    const handleButtonClick = useCallback(() => {
        if (isDisabled) {
            return;
        }
        showModal();
    }, [showModal, isDisabled]);

    return (
        <>
            <div
                data-testid="lens-delete-btn"
                role="button"
                tabIndex={0}
                aria-disabled={isDisabled}
                onClick={handleButtonClick}
                onKeyPress={onKeyPressModalTrigger}
            >
                {children}
            </div>
            <Modal
                data-testid="lens-delete-modal"
                {...modalProps}
                padBody={false}
                status="error"
                footer={
                    <Flexbox justifyContent="flex-end">
                        <Button onClick={modalProps.onCancel} isDisabled={deleteLensLoading}>
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            onClick={onClickDelete}
                            isPending={deleteLensLoading}
                            className={deleteButtonStyle}
                            isDisabled={isDisabled}
                        >
                            Delete
                        </Button>
                    </Flexbox>
                }
            >
                <DeleteLensModal>
                    <LensDeleteHeader>
                        <ModalHeader />
                    </LensDeleteHeader>
                    <LensDeleteText>
                        <ModalText />
                    </LensDeleteText>
                    {lensCreatorPayoutEligibility !==
                        LensCreatorPayoutEligibility.LensCreatorPayoutEligibilityUnset && (
                        <Alert
                            status="warning"
                            title={<LensCreatorPayoutAlertTitle />}
                            description={<LensCreatorPayoutAlertDescription />}
                        />
                    )}
                </DeleteLensModal>
            </Modal>
        </>
    );
};
